import React from "react";
import { isEmpty } from "lodash";
import moment from "moment";
import { useParams, useHistory } from "react-router-dom";
import { Row, Col, Input, InputGroup, InputGroupAddon, FormFeedback, Button, Card, Label } from "reactstrap";
import PageLayout from "../../components/PageLayout";
import Loading from "../../components/Loading";
import OrderSummary from "../../components/OrderSummary";
import { getOrder, updateOrder, createOrderDelivery, getDelivery, cancelOrderDelivery, setOrderStatus, updateDeliveryStatus } from "../../api/orders";
import { getTenant } from "../../api/tenants";
import { getDeliveryValidation } from "../../api/devices";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const OrderDelivery = ({ isBackOffice }) => {
    const { id } = useParams();
    const { onSuccess, onError } = useAuth0();
    const [scans, setScans] = React.useState([]);
    const [focused, setFocused] = React.useState("-");
    const [errors, setErrors] = React.useState({});
    const [busy, setBusy] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [tenant, setTenant] = React.useState();
    const [delivery, setDelivery] = React.useState();
    const [order, setOrder] = React.useState();
    const { push } = useHistory();

    React.useEffect(() => {
        loadData(id);
    }, [id || -1]);

    React.useEffect(() => {
        //console.log(focused);
        if (focused !== "-" && scans.length && !loading) {
            let elToFocus = document.getElementById(focused);
            if (elToFocus) {
                elToFocus.focus();
            }
        }
    }, [focused, scans.length, loading]);

    const resetForm = () => {
        setFocused("-");
        setErrors({});
        setTotalDevices(order.quantity);
    };

    const setTotalDevices = quantity => {
        const numDev = Number(quantity);
        const slots = new Array(numDev);
        slots.fill(null);
        setScans(slots);
        moveNext(slots);
    };

    const loadData = async id => {
        setLoading(true);
        try {
            const loadedOrder = await getOrder(id);
            let promises = [getTenant(loadedOrder.tenant_id), getDelivery(id)]
            const data = await Promise.all(promises)
            setOrder(loadedOrder);
            setTenant(data[0]);
            setDelivery(data[1]);
            setTotalDevices(loadedOrder.quantity);
        } finally {
            setLoading(false);
        }
    };
    const getDeviceId = i => (i > -1 ? `device-${i}` : "-");

    const validate = async scanlist => {
        try {
            setBusy(true);
            let valErrors = scanlist.reduce((err, el, i) => {
                const devId = getDeviceId(i);
                if (
                    !el ||
                    el.length !== 15 ||
                    scanlist.filter(dev => dev === el).length > 1
                ) {
                    err[
                        devId
                    ] = `Inserire un IMEI valido (lunghezza 15) e non inserito in precedenza`;
                }
                return err;
            }, {});

            if (!isEmpty(valErrors)) {
                setErrors(valErrors);
            } else {
                valErrors = await getDeliveryValidation(scanlist, getDeviceId);
                setErrors(valErrors);
            }
            return isEmpty(valErrors);
        } finally {
            setBusy(false);
        }
    };

    const onCheckReturn = e => {
        if (e.key === "Enter") {
            moveNext(scans);
        }
    };

    const saveData = async () => {
        try {
            if (!scans.length) return;

            setBusy(true);
            const isValid = await validate(scans);
            if (!isValid) return;
            await setOrderStatus(id, "preparato");
            await createOrderDelivery({
                scans,
                order_id: order._id,
                address: tenant.address,
                meta: {
                    tenant_id: tenant._id,
                    tenant_name: tenant.name
                }
            });
            await loadData(order._id);
            onSuccess();
            resetForm();
        } catch (error) {
            onError();
        } finally {
            setBusy(false);
        }
    };

    const confirmDelivery = async () => {
        try {
            const answer = prompt("Inserire il numero di spedizione (DHL waybill)");
            if (!answer) return;
            const answer2 = prompt("Confermare la data di spedizione", moment().format("DD/MM/YYYY"));
            if (!answer2) return;

            setBusy(true);
            order.waybill = answer;
            order.shipment_date = answer2;
            order.status = "spedito";
            await updateOrder(order);
            delivery.status = "spedito";
            await updateDeliveryStatus(delivery);
            onSuccess();
            push(`/tenant/orders`);
        } catch (error) {
            onError();
        } finally {
            setBusy(false);
        }
    };

    const closeOrder = async () => {
        if (!confirm("Confermi l'avvenuta consegna dell'ordine al cliente?")) return;
        try {
            const answer = prompt("Inserire la data di consegna", moment().format("DD/MM/YYYY"));
            if (!answer) return;

            setBusy(true);
            order.status = "consegnato";
            order.delivered_date = answer;
            await updateOrder(order);
            delivery.status = "consegnato";
            await updateDeliveryStatus(delivery);
            onSuccess();
            push(`/tenant/orders`);
        } finally {
            setBusy(false);
        }
    }

    const cancelOrder = async () => {
        if (!confirm("Confermi l'annullamento dell'ordine?")) return;
        try {
            setBusy(true);
            await cancelOrderDelivery(delivery);
            await loadData(delivery.order_id);
            onSuccess();
        } finally {
            setBusy(false);
        }
    }

    const moveNext = list => {
        let nextIndex = -1;
        const firstEmptySlot = list.findIndex(el => !el);
        if (firstEmptySlot > -1) {
            nextIndex = firstEmptySlot;
        }

        if (nextIndex === -1 && scans) {
        } else {
            setFocused(getDeviceId(nextIndex));
        }
    };

    const getScanner = () => {
        if (order.status !== "lavorazione") return null;
        return (
            <Row>
                {scans.map((el, i) => {
                    const idDevice = getDeviceId(i);
                    const validDevice = errors[idDevice] ? false : true;
                    return (
                        <Col sm="6" md="6" className="mt-2" key={i}>
                            <strong className="mt-2">Inserire gli IMEI dei dispositivi da associare all'ordine:</strong>
                            <InputGroup>
                                <InputGroupAddon addonType="prepend">IMEI</InputGroupAddon>
                                <Input
                                    id={idDevice}
                                    tabIndex={i + 1}
                                    disabled={busy}
                                    value={el || ""}
                                    maxLength={15}
                                    onKeyPress={onCheckReturn}
                                    onFocus={() => setFocused(idDevice)}
                                    onChange={e => onChange(e.target.value, i)}
                                    invalid={!validDevice}
                                />
                                {!validDevice && (
                                    <FormFeedback invalid="invalid">
                                        {errors[idDevice]}
                                    </FormFeedback>
                                )}
                            </InputGroup>
                        </Col>
                    );
                })}
            </Row>
        );
    };

    const getConfirmDeliveryActionButtons = () => {
        if (order.status !== "spedito") return null;
        return (
            <Row>
                <Col>
                    <div className="float-left mb-2">
                        <Button
                            color="success"
                            label="conferma consegnato"
                            onClick={closeOrder}
                        >
                            <FontAwesomeIcon icon="check" /> Conferma consegnato
                        </Button>
                    </div>
                </Col>
            </Row>
        );
    };

    const getCreateDeliveryActionButtons = () => {
        if (order.status !== "lavorazione") return null;
        return (
            <Row>
                <Col sm="6" md="6" className="mt-2">
                    <div className="float-left">
                        <Button
                            color="secondary"
                            className="mr-2"
                            disabled={busy}
                            onClick={resetForm}
                        >
                            <FontAwesomeIcon icon="times" /> Annulla IMEI
                        </Button>

                        <Button
                            disabled={busy || !isEmpty(errors)}
                            color="success"
                            onClick={saveData}
                        >
                            <FontAwesomeIcon spin={busy} icon={busy ? "spinner" : "check"} /> Conferma IMEI
                        </Button>
                    </div>
                </Col>
            </Row>
        );
    };

    const getAcceptDeliveryActionButtons = () => {
        if (order.status !== "preparato") return null;
        return (
            <Row>
                <Col>
                    <div className="float-left mb-2">
                        <Button
                            color="success"
                            className="mr-2"
                            disabled={busy}
                            onClick={confirmDelivery}
                        >
                            <FontAwesomeIcon spin={busy} icon={busy ? "spinner" : "check"} /> Conferma spedizione
                        </Button>
                        {isBackOffice && (
                            <Button
                                color="danger"
                                disabled={busy}
                                onClick={cancelOrder}
                            >
                                <FontAwesomeIcon icon="times" /> Cancella ordine
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>
        );
    };

    const onChange = async (value, index) => {
        try {
            const newScans = scans.map((el, i) => {
                return i === index ? value : el;
            });
            setScans(newScans);
            await validate(newScans);
        } catch (error) {
            console.log(error);
            onError();
        }
    };

    if (loading) {
        return <Loading />;
    }

    if (!order) {
        return null;
    }

    return (
        <PageLayout
            optionalNav={{ link: `/${isBackOffice ? "backoffice" : "tenant"}/orders`, text: "Ordini" }}
            title={`Ordine numero: ${order._id}`}
        >
            <React.Fragment>
                <Card body className="rounded bg-secondary mb-2">
                    <Row>
                        <Col sm="4" xs="12">
                            <strong>Dealer: </strong>
                            <Label className="h5">{tenant.name}</Label>
                        </Col>
                        <Col sm="4" xs="12" md="3">
                            <strong>Telefono: </strong>
                            <Label className="h5">{tenant.phone}</Label>
                        </Col>
                        <Col sm="4" xs="12" md="3">
                            <strong>Email: </strong>
                            <Label className="h5">{tenant.email}</Label>
                        </Col>
                    </Row>
                </Card>
            </React.Fragment>
            {getAcceptDeliveryActionButtons()}
            {getConfirmDeliveryActionButtons()}
            <OrderSummary tenant={tenant} order={order} delivery={delivery} isBackOffice={isBackOffice} />
            {getCreateDeliveryActionButtons()}
            {getScanner()}
            <Button
                onClick={() => {
                    history.go(-1);
                }}
                color="warning"
                className="btn btn-warning mt-2 float-right"
            >
                <FontAwesomeIcon icon="reply" /> Torna
            </Button>

        </PageLayout>
    );
};
export default OrderDelivery;
